/**
 * Marketing Attribution Utility
 * Handles UTM parameters, promo codes, and campaign source detection
 */

export interface MarketingAttribution {
  campaign_source: string;
  promo_code?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
}

const STORAGE_KEYS = {
  CAMPAIGN_SOURCE: 'bluelux_campaign_source',
  PROMO_CODE: 'bluelux_promo_code',
  UTM_SOURCE: 'bluelux_utm_source',
  UTM_MEDIUM: 'bluelux_utm_medium',
  UTM_CAMPAIGN: 'bluelux_utm_campaign',
  UTM_CONTENT: 'bluelux_utm_content',
  UTM_TERM: 'bluelux_utm_term',
  REFERRER: 'bluelux_referrer',
  LANDING_PAGE: 'bluelux_landing_page',
};

/**
 * Initialize marketing attribution tracking on first page load
 * Call this in your main app entry point or layout
 */
export const initializeMarketingAttribution = () => {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  const referrer = document.referrer;

  // Only set landing page on first visit (no stored data yet)
  if (!sessionStorage.getItem(STORAGE_KEYS.LANDING_PAGE)) {
    sessionStorage.setItem(STORAGE_KEYS.LANDING_PAGE, window.location.href);
  }

  // Capture UTM parameters
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');
  const utmContent = urlParams.get('utm_content');
  const utmTerm = urlParams.get('utm_term');

  // Capture referral source
  const refParam = urlParams.get('ref') || urlParams.get('source');

  // Capture promo code
  const promoParam = urlParams.get('promo') || urlParams.get('code');

  // Store UTM parameters (overwrite if present)
  if (utmSource) {
    sessionStorage.setItem(STORAGE_KEYS.UTM_SOURCE, utmSource);
    sessionStorage.setItem(STORAGE_KEYS.CAMPAIGN_SOURCE, utmSource);
  }
  if (utmMedium) sessionStorage.setItem(STORAGE_KEYS.UTM_MEDIUM, utmMedium);
  if (utmCampaign) sessionStorage.setItem(STORAGE_KEYS.UTM_CAMPAIGN, utmCampaign);
  if (utmContent) sessionStorage.setItem(STORAGE_KEYS.UTM_CONTENT, utmContent);
  if (utmTerm) sessionStorage.setItem(STORAGE_KEYS.UTM_TERM, utmTerm);

  // Store referral parameter
  if (refParam) {
    sessionStorage.setItem(STORAGE_KEYS.CAMPAIGN_SOURCE, refParam);
  }

  // Store promo code
  if (promoParam) {
    sessionStorage.setItem(STORAGE_KEYS.PROMO_CODE, promoParam);
  }

  // Store referrer (only if not already stored and not empty)
  if (referrer && !sessionStorage.getItem(STORAGE_KEYS.REFERRER)) {
    sessionStorage.setItem(STORAGE_KEYS.REFERRER, referrer);
  }
};

/**
 * Get current marketing attribution data
 * Priority: URL params > Session storage > Defaults
 */
export const getMarketingAttribution = (): MarketingAttribution => {
  if (typeof window === 'undefined') {
    return { campaign_source: 'direct' };
  }

  const urlParams = new URLSearchParams(window.location.search);

  // Get campaign source with priority
  const campaign_source = 
    urlParams.get('utm_source') ||
    urlParams.get('ref') ||
    urlParams.get('source') ||
    sessionStorage.getItem(STORAGE_KEYS.CAMPAIGN_SOURCE) ||
    'direct';

  // Get promo code
  const promo_code = 
    urlParams.get('promo') ||
    urlParams.get('code') ||
    urlParams.get('utm_campaign') ||
    sessionStorage.getItem(STORAGE_KEYS.PROMO_CODE) ||
    undefined;

  // Get all UTM parameters
  const utm_source = 
    urlParams.get('utm_source') ||
    sessionStorage.getItem(STORAGE_KEYS.UTM_SOURCE) ||
    undefined;

  const utm_medium = 
    urlParams.get('utm_medium') ||
    sessionStorage.getItem(STORAGE_KEYS.UTM_MEDIUM) ||
    undefined;

  const utm_campaign = 
    urlParams.get('utm_campaign') ||
    sessionStorage.getItem(STORAGE_KEYS.UTM_CAMPAIGN) ||
    undefined;

  const utm_content = 
    urlParams.get('utm_content') ||
    sessionStorage.getItem(STORAGE_KEYS.UTM_CONTENT) ||
    undefined;

  const utm_term = 
    urlParams.get('utm_term') ||
    sessionStorage.getItem(STORAGE_KEYS.UTM_TERM) ||
    undefined;

  const referrer = sessionStorage.getItem(STORAGE_KEYS.REFERRER) || undefined;

  return {
    campaign_source,
    promo_code,
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    referrer,
  };
};

/**
 * Get landing page URL
 */
export const getLandingPage = (): string | null => {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(STORAGE_KEYS.LANDING_PAGE);
};

/**
 * Clear marketing attribution data (useful for testing)
 */
export const clearMarketingAttribution = () => {
  if (typeof window === 'undefined') return;
  
  Object.values(STORAGE_KEYS).forEach(key => {
    sessionStorage.removeItem(key);
  });
};

/**
 * Get marketing attribution metadata for lead submissions
 * Returns a JSON object with all UTM and tracking data
 */
export const getMarketingMetadata = () => {
  const attribution = getMarketingAttribution();
  const landingPage = getLandingPage();

  return {
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    utm_content: attribution.utm_content,
    utm_term: attribution.utm_term,
    referrer: attribution.referrer,
    landing_page: landingPage,
  };
};
